import React from "react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import PageSeo from "../components/site/PageSeo";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import { ArrowUpRight } from "lucide-react";
import { SITE_URL, ORGANIZATION, PERSON, SOCIAL_PROFILES } from "../data/entityGraph";
import { trackEvent } from "../lib/track";

const ORDER = ["linkedin", "instagram", "tiktok", "substack", "medium", "x"];

const ConnectPage = () => {
  const profiles = ORDER.map((k) => SOCIAL_PROFILES[k]).filter(Boolean);

  const collectionLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": `${SITE_URL}/connect#collection`,
    url: `${SITE_URL}/connect`,
    name: "Connect - Authentic Momentum",
    about: { ...PERSON },
    mainEntity: { ...PERSON },
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Connect", item: `${SITE_URL}/connect` },
    ],
  };

  return (
    <>
      <PageSeo
        title="Connect | Authentic Momentum and Cyrus Prescod"
        description="The official link hub for Authentic Momentum and Cyrus Prescod. LinkedIn, Instagram, TikTok, Substack, Medium, and more."
        canonical={`${SITE_URL}/connect`}
        jsonLd={[collectionLd, breadcrumbLd, { "@context": "https://schema.org", ...ORGANIZATION }]}
      />
      <Navigation />
      <main className="relative pt-32 pb-32 noise-overlay min-h-screen">
        <div className="absolute inset-0 gold-glow-radial pointer-events-none" aria-hidden />

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              Connect
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-themed leading-tight tracking-tight font-light">
              Every official{" "}
              <span className="italic gold-gradient-text">link.</span>
            </h1>
            <p className="mt-7 text-white/85 leading-relaxed max-w-xl mx-auto text-[17px]">
              The places to follow the work, read the next dispatch, and stay close to the signal.
            </p>
          </Reveal>
        </section>

        <section
          data-testid="connect-links"
          className="relative max-w-2xl mx-auto px-6 md:px-10 mt-16 space-y-3"
        >
          {profiles.map((p, i) => {
            const isActive = p.active && p.url;
            const Tag = isActive ? "a" : "div";
            const props = isActive
              ? {
                  href: p.url,
                  target: "_blank",
                  rel: "noopener me noreferrer",
                  onClick: () => trackEvent("connect_link_clicked", { profile: p.label }),
                }
              : {};
            return (
              <Reveal key={p.label} delay={80 + i * 60}>
                <Tag
                  {...props}
                  data-testid={`connect-link-${p.label.toLowerCase()}`}
                  data-active={isActive ? "true" : "false"}
                  className={`block bg-white/5 border-t border-gold/20 p-5 md:p-6 rounded-lg border border-gold/10 ${
                    isActive ? "hover:border-gold/40 transition-colors group" : "opacity-60"
                  }`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-[10px] tracking-[0.4em] uppercase text-gold/85">
                        {p.label}
                      </p>
                      <p className={`mt-1 text-[15px] ${isActive ? "text-themed group-hover:text-gold transition-colors" : "text-themed-muted"}`}>
                        {p.handle}
                      </p>
                    </div>
                    {isActive ? (
                      <ArrowUpRight size={16} className="text-gold/70 group-hover:text-gold transition-colors shrink-0" />
                    ) : (
                      <span className="text-[10px] tracking-[0.3em] uppercase text-themed-muted/70 shrink-0">
                        Coming soon
                      </span>
                    )}
                  </div>
                </Tag>
              </Reveal>
            );
          })}
        </section>

        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-20 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-4">Or Come Closer</p>
            <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
              <a href="/cyrus-prescod" data-testid="connect-cta-cyrus" className="btn-ghost-gold inline-flex">
                Founder Profile
              </a>
              <a href="/framework" data-testid="connect-cta-framework" className="btn-ghost-gold inline-flex">
                Read the Framework
              </a>
              <a href="/press" data-testid="connect-cta-press" className="btn-ghost-gold inline-flex">
                Press
              </a>
            </div>
          </Reveal>
        </section>
      </main>
      <Footer />
      <OrientationResetCheckoutHost />
    </>
  );
};

export default ConnectPage;
